import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Globe, Download, QrCode, ArrowUpRight } from "lucide-react";
import WhatsAppIcon from "@/components/card/WhatsAppIcon";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
  }),
};

const Chapter = ({ num, label }) => (
  <motion.div
    variants={reveal}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-80px" }}
    className="mb-10 flex items-center gap-4"
  >
    <span className="font-mono-accent text-xs uppercase tracking-[0.35em] text-white/50">
      {num} // {label}
    </span>
    <span className="h-px flex-1 bg-white/10" />
  </motion.div>
);

const contactRow = (icon, label, value, href, testid) => (
  <a
    href={href}
    target={href?.startsWith("http") ? "_blank" : undefined}
    rel="noreferrer"
    data-testid={testid}
    className="group flex items-center justify-between border-b border-white/10 py-5 transition-colors duration-300 hover:border-white/40"
  >
    <span className="flex items-center gap-4">
      <span className="text-white/40 transition-colors duration-300 group-hover:text-white">{icon}</span>
      <span className="flex flex-col">
        <span className="font-mono-accent text-[10px] uppercase tracking-widest text-white/40">{label}</span>
        <span className="text-base text-white md:text-lg">{value}</span>
      </span>
    </span>
    <ArrowUpRight className="h-5 w-5 text-white/30 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-white" />
  </a>
);

export default function BentoDetails({ card, onSave, onShare }) {
  return (
    <section className="mx-auto max-w-6xl px-6 py-28 md:px-10 md:py-40" data-testid="details-section">
      <Chapter num="01" label="Connect" />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
        {/* Contact info */}
        <motion.div
          custom={0}
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="crystal rounded-3xl p-8 md:col-span-7 md:p-10"
        >
          <h3 className="font-display mb-4 text-3xl font-medium tracking-tight md:text-4xl">
            Get in touch
          </h3>
          <div className="flex flex-col">
            {contactRow(<Mail className="h-5 w-5" />, "Email", card.email, `mailto:${card.email}`, "contact-email")}
            {contactRow(<Phone className="h-5 w-5" />, "Phone", card.phone, `tel:${card.phone}`, "contact-phone")}
            {card.whatsapp &&
              contactRow(
                <WhatsAppIcon className="h-5 w-5" />,
                "WhatsApp",
                card.whatsapp,
                `https://wa.me/${card.whatsapp.replace(/[^0-9]/g, "")}`,
                "contact-whatsapp"
              )}
            {contactRow(<Globe className="h-5 w-5" />, "Website", card.websiteLabel || card.website?.replace(/^https?:\/\//, ""), card.website, "contact-website")}
            {contactRow(<MapPin className="h-5 w-5" />, "Location", card.location, `https://maps.google.com/?q=${encodeURIComponent(card.location || "")}`, "contact-location")}
          </div>
        </motion.div>

        {/* Actions */}
        <motion.div
          custom={1}
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="flex flex-col gap-6 md:col-span-5"
        >
          <div className="crystal flex flex-1 flex-col justify-between rounded-3xl p-8">
            <div>
              <span className="font-mono-accent text-[10px] uppercase tracking-widest text-white/40">Take me with you</span>
              <p className="mt-3 text-white/70">Add my details straight to your phone or share this card in a tap.</p>
            </div>
            <div className="mt-8 flex flex-col gap-3">
              <button
                onClick={onSave}
                data-testid="details-save-contact-btn"
                className="flex items-center justify-center gap-3 rounded-full bg-white py-4 text-sm font-medium text-[#050505] transition-transform duration-300 hover:scale-[1.02] active:scale-95"
              >
                <Download className="h-4 w-4" /> Save Contact (.vcf)
              </button>
              <button
                onClick={onShare}
                data-testid="details-share-btn"
                className="flex items-center justify-center gap-3 rounded-full border border-white/20 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-white/10"
              >
                <QrCode className="h-4 w-4" /> Show QR Code
              </button>
            </div>
          </div>
        </motion.div>

        {/* Channels */}
        <motion.div
          custom={2}
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="md:col-span-12"
        >
          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
            {[
              card.whatsapp && {
                label: "WhatsApp",
                handle: card.whatsapp,
                url: `https://wa.me/${card.whatsapp.replace(/[^0-9]/g, "")}`,
                icon: <WhatsAppIcon className="h-6 w-6 text-[#25D366]" />,
              },
              { label: "Website", handle: card.websiteLabel, url: card.website, icon: <Globe className="h-6 w-6 text-white/70" /> },
              { label: "Email", handle: card.email, url: `mailto:${card.email}`, icon: <Mail className="h-6 w-6 text-white/70" /> },
              { label: "Call", handle: card.phone, url: `tel:${card.phone}`, icon: <Phone className="h-6 w-6 text-white/70" /> },
            ]
              .filter(Boolean)
              .map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target={s.url?.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  data-testid={`channel-${s.label.toLowerCase()}`}
                  className="crystal group flex flex-col gap-6 rounded-2xl p-6 transition-colors duration-300 hover:bg-white/10"
                >
                  <span className="flex items-center justify-between">
                    {s.icon}
                    <ArrowUpRight className="h-5 w-5 text-white/30 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-white" />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-base font-medium text-white">{s.label}</span>
                    <span className="font-mono-accent text-xs text-white/40">{s.handle}</span>
                  </span>
                </a>
              ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
